import React from 'react';
import { getTicketStats } from '../../../../shared/services/data/ticket.data';
import { searchLeads } from '../../../../shared/services/data/lead.data';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { cn } from '../../../../utils/cn';
import { DashboardGoal, GoalKey, LeadVertical } from '../../../shared/config/role-dashboard.config';
import { CARD, CHIP } from './dashboard-ui';

export type GoalValues = Partial<Record<GoalKey, number>>;

const leadsTotal = (
  verticals: LeadVertical[],
  query: { status?: string; slaBreachedOnly?: boolean }
) =>
  Promise.all(
    (verticals.length ? verticals : [undefined]).map((vertical) =>
      searchLeads({ ...query, vertical }, 1, 0)
    )
  ).then((results) =>
    results.some(isErrorInfo)
      ? undefined
      : (results as { total: number }[]).reduce((sum, r) => sum + r.total, 0)
  );

export function loadGoalValues(
  goals: DashboardGoal[],
  verticals: LeadVertical[]
): Promise<GoalValues> {
  const keys = goals.map((g) => g.key);
  const wantsTickets =
    keys.includes('ticketsClosedToday') || keys.includes('awaitingFirstResponse');
  return Promise.all([
    wantsTickets ? getTicketStats('day') : undefined,
    keys.includes('leadsPastSla') ? leadsTotal(verticals, { slaBreachedOnly: true }) : undefined,
    keys.includes('newLeads') ? leadsTotal(verticals, { status: 'new' }) : undefined
  ]).then(([stats, pastSla, newLeads]) => {
    const values: GoalValues = { leadsPastSla: pastSla, newLeads };
    if (stats && !isErrorInfo(stats)) {
      values.ticketsClosedToday = stats.totals.closed;
      values.awaitingFirstResponse = stats.awaitingResponse;
    }
    return values;
  });
}

type GoalsRowProps = {
  goals: DashboardGoal[];
  values: GoalValues | null;
};

export const GoalsRow = ({ goals, values }: GoalsRowProps) => (
  <div
    className={cn(
      'grid grid-cols-1 gap-4 sm:grid-cols-2',
      goals.length >= 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'
    )}
  >
    {goals.map((goal) => {
      const value = values?.[goal.key];
      const known = typeof value === 'number';
      const onTrack = known && (goal.lowerIsBetter ? value! <= goal.target : value! >= goal.target);
      const progress = !known
        ? 0
        : goal.lowerIsBetter
        ? value! <= goal.target
          ? 100
          : Math.max(8, 100 - ((value! - goal.target) / Math.max(value!, 1)) * 100)
        : Math.min(100, (value! / Math.max(goal.target, 1)) * 100);
      return (
        <div key={goal.key} className={cn(CARD, 'p-4')}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] text-neutral-300">{goal.label}</span>
            {known && (
              <span
                className={cn(
                  CHIP,
                  onTrack ? 'bg-primary-50 text-primary-700' : 'bg-secondary-100 text-secondary-800'
                )}
              >
                {onTrack ? 'On track' : 'Behind'}
              </span>
            )}
          </div>
          {values === null ? (
            <span className="mt-3 block h-[28px] w-20 animate-pulse rounded bg-gray-200 motion-reduce:animate-none" />
          ) : (
            <p className="m-0 mt-2.5 text-[28px] font-gilroy-bold leading-none tabular-nums text-neutral-500">
              {known ? value!.toLocaleString() : '—'}
              <span className="text-[15px] font-gilroy-medium text-neutral-300">
                {' '}
                / {goal.lowerIsBetter ? '≤ ' : ''}
                {goal.target.toLocaleString()}
              </span>
            </p>
          )}
          <div className="mt-3.5 h-1.5 overflow-hidden rounded-full bg-creams-300">
            <div
              className={cn('h-full rounded-full', onTrack ? 'bg-primary-500' : 'bg-secondary-500')}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      );
    })}
  </div>
);
